import { useState, useEffect } from "react";
const API_BASE_URL = "/api/v1";
import { useAdmin } from "../../hooks/useAdmin";
import { useMidnightSettings, useUpdateMidnightSettings } from "../../hooks/useMidnight";
import { useTodaysMenu } from "../../hooks/useMenu";

const DAYS_OF_WEEK = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

export function MenuPage() {
  const [activeTab, setActiveTab] = useState<"weeks" | "special-weeks" | "toppings" | "specials" | "midnight" | "breakfast">("weeks");

  return (
    <div className="bg-white rounded-lg shadow">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="flex -mb-px">
          <button
            onClick={() => setActiveTab("weeks")}
            className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "weeks"
                ? "border-[#007FA3] text-[#007FA3]"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Weekly Menu
          </button>
          <button
            onClick={() => setActiveTab("special-weeks")}
            className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "special-weeks"
                ? "border-[#007FA3] text-[#007FA3]"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Special Weeks
          </button>
          <button
            onClick={() => setActiveTab("toppings")}
            className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "toppings"
                ? "border-[#007FA3] text-[#007FA3]"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Toppings
          </button>
          <button
            onClick={() => setActiveTab("specials")}
            className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "specials"
                ? "border-[#007FA3] text-[#007FA3]"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Daily Specials
          </button>
          <button
            onClick={() => setActiveTab("midnight")}
            className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "midnight"
                ? "border-[#007FA3] text-[#007FA3]"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            🌙 Midnight Menu
          </button>
          <button
            onClick={() => setActiveTab("breakfast")}
            className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "breakfast"
                ? "border-[#007FA3] text-[#007FA3]"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Breakfast Menu
          </button>
        </nav>
      </div>

      {/* Tab Content */}
      <div className="p-8">
        {activeTab === "weeks" && <WeeklyMenuTab />}
        {activeTab === "special-weeks" && <SpecialWeeksTab />}
        {activeTab === "toppings" && <ToppingsTab />}
        {activeTab === "specials" && <DailySpecialsTab />}
        {activeTab === "midnight" && <MidnightMenuTab />}
        {activeTab === "breakfast" && <BreakfastMenuTab />}
      </div>
    </div>
  );
}

// Weekly Menu Tab Component
function WeeklyMenuTab() {
  // Get current week from the API
  const { menu: todaysMenu } = useTodaysMenu();
  const currentWeek = todaysMenu?.week_number || 1;

  const [selectedWeek, setSelectedWeek] = useState(currentWeek);
  const [editingItem, setEditingItem] = useState<number | null>(null);
  const [optionsFor, setOptionsFor] = useState<{ kind: string; id: number; name: string } | null>(null);
  const [editValues, setEditValues] = useState({ menu_item: "", description: "" });
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItem, setNewItem] = useState({ day_of_week: 1, menu_item: "", description: "" });

  // Update selected week when current week is loaded
  useEffect(() => {
    if (currentWeek) {
      setSelectedWeek(currentWeek);
    }
  }, [currentWeek]);

  const { data, isLoading } = useAdmin.useMenuWeeks();
  const updateMutation = useAdmin.useUpdateMenuItem();
  const createMutation = useAdmin.useCreateMenuItem();
  const deleteMutation = useAdmin.useDeleteMenuItem();

  const weekItems = data?.weeks[selectedWeek] || [];

  const handleEdit = (item: any) => {
    setEditingItem(item.id);
    setEditValues({ menu_item: item.menu_item, description: item.description || "" });
  };

  const handleSave = async (itemId: number) => {
    await updateMutation.mutateAsync({ itemId, data: editValues });
    setEditingItem(null);
  };

  const handleToggleActive = async (itemId: number, currentStatus: boolean) => {
    await updateMutation.mutateAsync({ itemId, data: { is_active: !currentStatus } });
  };

  const handleDelete = async (itemId: number) => {
    if (confirm("Are you sure you want to delete this menu item?")) {
      await deleteMutation.mutateAsync(itemId);
    }
  };

  const handleAddItem = async () => {
    await createMutation.mutateAsync({
      week_number: selectedWeek,
      day_of_week: newItem.day_of_week,
      menu_item: newItem.menu_item,
      description: newItem.description || undefined,
    });
    setShowAddForm(false);
    setNewItem({ day_of_week: 1, menu_item: "", description: "" });
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading menu weeks...</div>;
  }

  return (
    <div>
      {optionsFor && (<ItemOptionsEditor target={optionsFor} onClose={() => setOptionsFor(null)} />)}
      {/* Week Selector */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex gap-2">
          {[1, 2, 3, 4, 5].map((week) => (
            <button
              key={week}
              onClick={() => setSelectedWeek(week)}
              className={`px-4 py-2 rounded font-medium transition-colors relative ${
                selectedWeek === week
                  ? "bg-[#007FA3] text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Week {week}
              {week === currentWeek && (
                <span className="ml-1.5 inline-flex items-center justify-center w-2 h-2 rounded-full bg-green-400 absolute -top-1 -right-1">
                  <span className="sr-only">Current week</span>
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-4">
          {currentWeek && (
            <div className="text-sm text-gray-600 flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-green-400"></span>
              <span>Current Week: {currentWeek}</span>
            </div>
          )}
          <button
            onClick={() => setShowAddForm(true)}
            className="px-4 py-2 bg-[#62B5E5] text-white rounded hover:bg-[#007FA3] transition-colors"
          >
            Add Menu Item
          </button>
        </div>
      </div>

      {/* Add Item Form */}
      {showAddForm && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-4">Add New Menu Item</h3>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Day of Week</label>
              <select
                value={newItem.day_of_week}
                onChange={(e) => setNewItem({ ...newItem, day_of_week: Number(e.target.value) })}
                className="w-full border border-gray-300 rounded px-3 py-2"
              >
                {DAYS_OF_WEEK.map((day, idx) => (
                  <option key={idx} value={idx}>
                    {day}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Menu Item</label>
              <input
                type="text"
                value={newItem.menu_item}
                onChange={(e) => setNewItem({ ...newItem, menu_item: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2"
                placeholder="e.g., Chicken Caesar Salad"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2"
                placeholder="Optional description"
              />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button
              onClick={handleAddItem}
              disabled={!newItem.menu_item}
              className="px-4 py-2 bg-[#007FA3] text-white rounded hover:bg-[#005f7a] disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Add Item
            </button>
            <button
              onClick={() => {
                setShowAddForm(false);
                setNewItem({ day_of_week: 1, menu_item: "", description: "" });
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Menu Items Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 font-semibold text-gray-700">Day</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700">Menu Item</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700">Description</th>
              <th className="text-center py-3 px-4 font-semibold text-gray-700">Active</th>
              <th className="text-right py-3 px-4 font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {DAYS_OF_WEEK.map((day, dayIdx) => {
              const item = weekItems.find((i) => i.day_of_week === dayIdx);
              const isEditing = editingItem === item?.id;

              return (
                <tr key={dayIdx} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4 font-medium text-gray-900">{day}</td>
                  <td className="py-3 px-4">
                    {item ? (
                      isEditing ? (
                        <input
                          type="text"
                          value={editValues.menu_item}
                          onChange={(e) => setEditValues({ ...editValues, menu_item: e.target.value })}
                          className="w-full border border-gray-300 rounded px-2 py-1"
                        />
                      ) : (
                        <span className={item.is_active ? "text-gray-900" : "text-gray-400"}>
                          {item.menu_item}
                        </span>
                      )
                    ) : (
                      <span className="text-gray-400 italic">No item</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    {item ? (
                      isEditing ? (
                        <input
                          type="text"
                          value={editValues.description}
                          onChange={(e) => setEditValues({ ...editValues, description: e.target.value })}
                          className="w-full border border-gray-300 rounded px-2 py-1"
                        />
                      ) : (
                        <span className={item.is_active ? "text-gray-600 text-sm" : "text-gray-400 text-sm"}>
                          {item.description || "-"}
                        </span>
                      )
                    ) : null}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {item && (
                      <button
                        onClick={() => handleToggleActive(item.id, item.is_active)}
                        className={`px-3 py-1 rounded text-xs font-medium ${
                          item.is_active
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {item.is_active ? "Active" : "Inactive"}
                      </button>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right">
                    {item && (
                      <div className="flex gap-2 justify-end">
                        {isEditing ? (
                          <>
                            <button
                              onClick={() => handleSave(item.id)}
                              className="text-[#007FA3] hover:text-[#005f7a] font-medium text-sm"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingItem(null)}
                              className="text-gray-600 hover:text-gray-800 font-medium text-sm"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => handleEdit(item)}
                              className="text-[#007FA3] hover:text-[#005f7a] font-medium text-sm"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => setOptionsFor({ kind: "menu_week", id: item.id, name: item.menu_item })}
                              className="text-indigo-600 hover:text-indigo-800 font-medium text-sm"
                            >
                              Options
                            </button>
                            <button
                              onClick={() => handleDelete(item.id)}
                              className="text-red-600 hover:text-red-800 font-medium text-sm"
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Toppings Tab Component
function ToppingsTab() {
  const [editingTopping, setEditingTopping] = useState<number | null>(null);
  const [editValues, setEditValues] = useState({ name: "", category: "", sort_order: 0 });
  const [showAddForm, setShowAddForm] = useState(false);
  const [newTopping, setNewTopping] = useState({ name: "", category: "protein", sort_order: 0 });

  const { data, isLoading } = useAdmin.useToppings();
  const updateMutation = useAdmin.useUpdateTopping();
  const createMutation = useAdmin.useCreateTopping();

  const toppingsByCategory = data?.toppings.reduce((acc: any, topping: any) => {
    if (!acc[topping.category]) acc[topping.category] = [];
    acc[topping.category].push(topping);
    return acc;
  }, {}) || {};

  const handleEdit = (topping: any) => {
    setEditingTopping(topping.id);
    setEditValues({ name: topping.name, category: topping.category, sort_order: topping.sort_order });
  };

  const handleSave = async (toppingId: number) => {
    await updateMutation.mutateAsync({ toppingId, data: editValues });
    setEditingTopping(null);
  };

  const handleToggleAvailable = async (toppingId: number, currentStatus: boolean) => {
    await updateMutation.mutateAsync({ toppingId, data: { is_available: !currentStatus } });
  };

  const handleAddTopping = async () => {
    await createMutation.mutateAsync(newTopping);
    setShowAddForm(false);
    setNewTopping({ name: "", category: "protein", sort_order: 0 });
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading toppings...</div>;
  }

  return (
    <div>
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Manage Toppings</h2>
        <button
          onClick={() => setShowAddForm(true)}
          className="px-4 py-2 bg-[#62B5E5] text-white rounded hover:bg-[#007FA3] transition-colors"
        >
          Add Topping
        </button>
      </div>

      {/* Add Topping Form */}
      {showAddForm && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-4">Add New Topping</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input
                type="text"
                value={newTopping.name}
                onChange={(e) => setNewTopping({ ...newTopping, name: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2"
                placeholder="e.g., Grilled Chicken"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={newTopping.category}
                onChange={(e) => setNewTopping({ ...newTopping, category: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2"
              >
                <option value="protein">Protein</option>
                <option value="vegetable">Vegetable</option>
                <option value="cheese">Cheese</option>
                <option value="dressing">Dressing</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sort Order</label>
              <input
                type="number"
                value={newTopping.sort_order}
                onChange={(e) => setNewTopping({ ...newTopping, sort_order: Number(e.target.value) })}
                className="w-full border border-gray-300 rounded px-3 py-2"
              />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button
              onClick={handleAddTopping}
              disabled={!newTopping.name}
              className="px-4 py-2 bg-[#007FA3] text-white rounded hover:bg-[#005f7a] disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Add Topping
            </button>
            <button
              onClick={() => {
                setShowAddForm(false);
                setNewTopping({ name: "", category: "protein", sort_order: 0 });
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Toppings by Category */}
      <div className="space-y-6">
        {Object.entries(toppingsByCategory).map(([category, toppings]: [string, any]) => (
          <div key={category} className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900 capitalize">{category}</h3>
            </div>
            <div className="divide-y divide-gray-100">
              {toppings.map((topping: any) => {
                const isEditing = editingTopping === topping.id;
                return (
                  <div key={topping.id} className="px-4 py-3 hover:bg-gray-50 flex items-center justify-between">
                    <div className="flex-1 grid grid-cols-4 gap-4">
                      <div>
                        {isEditing ? (
                          <input
                            type="text"
                            value={editValues.name}
                            onChange={(e) => setEditValues({ ...editValues, name: e.target.value })}
                            className="w-full border border-gray-300 rounded px-2 py-1"
                          />
                        ) : (
                          <span className={topping.is_available ? "text-gray-900" : "text-gray-400"}>
                            {topping.name}
                          </span>
                        )}
                      </div>
                      <div>
                        {isEditing ? (
                          <select
                            value={editValues.category}
                            onChange={(e) => setEditValues({ ...editValues, category: e.target.value })}
                            className="w-full border border-gray-300 rounded px-2 py-1"
                          >
                            <option value="protein">Protein</option>
                            <option value="vegetable">Vegetable</option>
                            <option value="cheese">Cheese</option>
                            <option value="dressing">Dressing</option>
                            <option value="other">Other</option>
                          </select>
                        ) : (
                          <span className="text-gray-600 text-sm capitalize">{topping.category}</span>
                        )}
                      </div>
                      <div>
                        {isEditing ? (
                          <input
                            type="number"
                            value={editValues.sort_order}
                            onChange={(e) => setEditValues({ ...editValues, sort_order: Number(e.target.value) })}
                            className="w-24 border border-gray-300 rounded px-2 py-1"
                          />
                        ) : (
                          <span className="text-gray-600 text-sm">Order: {topping.sort_order}</span>
                        )}
                      </div>
                      <div>
                        <button
                          onClick={() => handleToggleAvailable(topping.id, topping.is_available)}
                          className={`px-3 py-1 rounded text-xs font-medium ${
                            topping.is_available
                              ? "bg-green-100 text-green-800"
                              : "bg-gray-100 text-gray-600"
                          }`}
                        >
                          {topping.is_available ? "Available" : "Unavailable"}
                        </button>
                      </div>
                    </div>
                    <div className="ml-4 flex gap-2">
                      {isEditing ? (
                        <>
                          <button
                            onClick={() => handleSave(topping.id)}
                            className="text-[#007FA3] hover:text-[#005f7a] font-medium text-sm"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingTopping(null)}
                            className="text-gray-600 hover:text-gray-800 font-medium text-sm"
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleEdit(topping)}
                          className="text-[#007FA3] hover:text-[#005f7a] font-medium text-sm"
                        >
                          Edit
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Helper function to format date string without timezone conversion
function formatDateString(dateStr: string): string {
  const [year, month, day] = dateStr.split("-");
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

// Daily Specials Tab Component
function DailySpecialsTab() {
  const [showAddForm, setShowAddForm] = useState(false);
  const [optionsFor, setOptionsFor] = useState<{ kind: string; id: number; name: string } | null>(null);
  const [newSpecial, setNewSpecial] = useState({
    date: "",
    menu_item: "",
    description: "",
    replacement_mode: 'add' as 'add' | 'replace_special' | 'replace_lunch',
    hide_salad: false,
    meal_period: 'lunch' as 'lunch' | 'midnight',
  });

  const today = new Date().toISOString().split("T")[0];
  const nextMonth = new Date();
  nextMonth.setMonth(nextMonth.getMonth() + 1);
  const nextMonthStr = nextMonth.toISOString().split("T")[0];

  const { data, isLoading, refetch } = useAdmin.useDailySpecials(today, nextMonthStr);
  const createMutation = useAdmin.useCreateDailySpecial();
  const deleteMutation = useAdmin.useDeleteDailySpecial();
  // Refetch when component mounts if we have a token
  useEffect(() => {
    const token = sessionStorage.getItem("hmh_access_token");
    if (token) {
      refetch();
    }
  }, [refetch]);

  const handleAddSpecial = async () => {
    await createMutation.mutateAsync(newSpecial);
    setShowAddForm(false);
    setNewSpecial({ date: "", menu_item: "", description: "", replacement_mode: 'add', hide_salad: false, meal_period: 'lunch' });
  };

  const handleDelete = async (specialId: number) => {
    if (confirm("Are you sure you want to delete this daily special?")) {
      await deleteMutation.mutateAsync(specialId);
    }
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading daily specials...</div>;
  }

  return (
    <div>
      {optionsFor && (<ItemOptionsEditor target={optionsFor} onClose={() => setOptionsFor(null)} />)}
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Daily Specials</h2>
        <button
          onClick={() => setShowAddForm(true)}
          className="px-4 py-2 bg-[#62B5E5] text-white rounded hover:bg-[#007FA3] transition-colors"
        >
          Add Special
        </button>
      </div>

      {/* Add Special Form */}
      {showAddForm && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-4">Add Daily Special</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
              <input
                type="date"
                value={newSpecial.date}
                onChange={(e) => setNewSpecial({ ...newSpecial, date: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Menu Item</label>
              <input
                type="text"
                value={newSpecial.menu_item}
                onChange={(e) => setNewSpecial({ ...newSpecial, menu_item: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2"
                placeholder="e.g., Thanksgiving Turkey Salad"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                value={newSpecial.description}
                onChange={(e) => setNewSpecial({ ...newSpecial, description: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2"
                placeholder="Optional description"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Meal Period</label>
              <div className="flex gap-4 mb-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="meal_period"
                    value="lunch"
                    checked={newSpecial.meal_period === 'lunch'}
                    onChange={() => setNewSpecial({ ...newSpecial, meal_period: 'lunch' })}
                    className="border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Lunch (day)</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="meal_period"
                    value="midnight"
                    checked={newSpecial.meal_period === 'midnight'}
                    onChange={() => setNewSpecial({ ...newSpecial, meal_period: 'midnight', replacement_mode: 'add', hide_salad: false })}
                    className="border-gray-300"
                  />
                  <span className="text-sm text-gray-700">🌙 Midnight (overnight shift)</span>
                </label>
              </div>
            </div>
            {newSpecial.meal_period === 'lunch' && (
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Replacement Mode</label>
              <div className="space-y-2">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="replacement_mode"
                    value="add"
                    checked={newSpecial.replacement_mode === 'add'}
                    onChange={(e) => setNewSpecial({ ...newSpecial, replacement_mode: e.target.value as 'add' })}
                    className="border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Add to existing Special Menu</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="replacement_mode"
                    value="replace_special"
                    checked={newSpecial.replacement_mode === 'replace_special'}
                    onChange={(e) => setNewSpecial({ ...newSpecial, replacement_mode: e.target.value as 'replace_special' })}
                    className="border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Replace existing Special Menu</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="replacement_mode"
                    value="replace_lunch"
                    checked={newSpecial.replacement_mode === 'replace_lunch'}
                    onChange={(e) => setNewSpecial({ ...newSpecial, replacement_mode: e.target.value as 'replace_lunch' })}
                    className="border-gray-300"
                  />
                  <span className="text-sm text-gray-700">Replace Lunch Menu</span>
                </label>
                {/* Hide salad checkbox - only visible when Replace Lunch Menu is selected */}
                {newSpecial.replacement_mode === 'replace_lunch' && (
                  <label className="flex items-center gap-2 ml-6 mt-2">
                    <input
                      type="checkbox"
                      checked={newSpecial.hide_salad}
                      onChange={(e) => setNewSpecial({ ...newSpecial, hide_salad: e.target.checked })}
                      className="border-gray-300 rounded"
                    />
                    <span className="text-sm text-gray-700">Also hide salad option</span>
                  </label>
                )}
              </div>
            </div>
            )}
          </div>
          <div className="mt-4 flex gap-2">
            <button
              onClick={handleAddSpecial}
              disabled={!newSpecial.date || !newSpecial.menu_item}
              className="px-4 py-2 bg-[#007FA3] text-white rounded hover:bg-[#005f7a] disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Add Special
            </button>
            <button
              onClick={() => {
                setShowAddForm(false);
                setNewSpecial({ date: "", menu_item: "", description: "", replacement_mode: 'add', hide_salad: false, meal_period: 'lunch' });
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Specials List */}
      <div className="space-y-3">
        {data?.specials && data.specials.length > 0 ? (
          data.specials.map((special: any) => {
            const getBadgeStyles = (mode: string) => {
              switch (mode) {
                case 'add':
                  return 'bg-green-100 text-green-800';
                case 'replace_special':
                  return 'bg-blue-100 text-blue-800';
                case 'replace_lunch':
                  return 'bg-orange-100 text-orange-800';
                default:
                  return 'bg-gray-100 text-gray-800';
              }
            };

            const getBadgeText = (mode: string) => {
              switch (mode) {
                case 'add':
                  return 'Added';
                case 'replace_special':
                  return 'Replaced Specials';
                case 'replace_lunch':
                  return 'Replaces Lunch';
                default:
                  return 'Unknown';
              }
            };

            const replacementMode = special.replacement_mode || (special.replaces_regular ? 'replace_lunch' : 'add');

            return (
              <div
                key={special.id}
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 flex items-start justify-between"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-sm font-medium text-gray-900">
                      {formatDateString(special.date)}
                    </span>
                    {special.meal_period === 'midnight' ? (
                      <span className="px-2 py-1 text-xs font-medium rounded bg-indigo-100 text-indigo-800">
                        🌙 Midnight
                      </span>
                    ) : (
                      <span className={`px-2 py-1 text-xs font-medium rounded ${getBadgeStyles(replacementMode)}`}>
                        {getBadgeText(replacementMode)}
                      </span>
                    )}
                    {special.hide_salad && (
                      <span className="px-2 py-1 text-xs font-medium rounded bg-red-100 text-red-800">
                        No Salad
                      </span>
                    )}
                  </div>
                  <h4 className="font-semibold text-gray-900 text-lg">{special.menu_item}</h4>
                  {special.description && (
                    <p className="text-gray-600 text-sm mt-1">{special.description}</p>
                  )}
                </div>
                <button
                  onClick={() => setOptionsFor({ kind: "daily_special", id: special.id, name: special.menu_item })}
                  className="ml-4 text-indigo-600 hover:text-indigo-800 font-medium text-sm"
                >
                  Options
                </button>
                <button
                  onClick={() => handleDelete(special.id)}
                  className="ml-4 text-red-600 hover:text-red-800 font-medium text-sm"
                >
                  Delete
                </button>
              </div>
            );
          })
        ) : (
          <div className="text-center py-8 text-gray-500">
            No daily specials scheduled. Add one to get started.
          </div>
        )}
      </div>
    </div>
  );
}

// Breakfast Menu Tab Component
function BreakfastMenuTab() {
  const [editingItem, setEditingItem] = useState<number | null>(null);
  const [editValues, setEditValues] = useState({ name: "", description: "", category: "", day_of_week: null as number | null, is_available: true });
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItem, setNewItem] = useState({ name: "", description: "", category: "entree", day_of_week: null as number | null, is_available: true });

  const { data, isLoading, error, refetch } = useAdmin.useBreakfastMenuItems();
  
  // Refetch when component mounts if we have a token
  useEffect(() => {
    const token = sessionStorage.getItem('hmh_access_token');
    if (token) {
      refetch();
    }
  }, [refetch]);
  
  console.log("Breakfast menu data:", data, "isLoading:", isLoading, "error:", error);
  const updateMutation = useAdmin.useUpdateBreakfastMenuItem();
  const createMutation = useAdmin.useCreateBreakfastMenuItem();
  const deleteMutation = useAdmin.useDeleteBreakfastMenuItem();
  // Helper function to get day name
  const getDayName = (dayOfWeek: number | null) => {
    if (dayOfWeek === null || dayOfWeek === undefined) return "Every Day";
    return DAYS_OF_WEEK[dayOfWeek];
  };


  // Group items by category
  const itemsByCategory = data?.items?.reduce((acc: any, item: any) => {
    if (!acc[item.category]) acc[item.category] = [];
    acc[item.category].push(item);
    return acc;
  }, {}) || {};

  const handleEdit = (item: any) => {
    setEditingItem(item.id);
    setEditValues({
      name: item.name,
      description: item.description || "",
      category: item.category,
      day_of_week: item.day_of_week,
      is_available: item.is_available
    });
  };

  const handleSave = async (itemId: number) => {
    await updateMutation.mutateAsync({ itemId, data: editValues });
    setEditingItem(null);
  };

  const handleToggleAvailable = async (itemId: number, currentStatus: boolean) => {
    await updateMutation.mutateAsync({ itemId, data: { is_available: !currentStatus } });
  };

  const handleDelete = async (itemId: number) => {
    if (confirm("Are you sure you want to delete this breakfast menu item?")) {
      try {
        await deleteMutation.mutateAsync(itemId);
      } catch (error: any) {
        // Try to get error message from response
        const message = error?.message || "Failed to delete item";
        alert(message.includes("order") ? message : "Cannot delete this item. It may have orders associated with it.");
      }
    }
  };

  const handleAddItem = async () => {
    await createMutation.mutateAsync(newItem);
    setShowAddForm(false);
    setNewItem({ name: "", description: "", category: "entree", day_of_week: null, is_available: true });
  };

  if (error) {
    return <div className="text-center py-8 text-red-600">Error loading breakfast menu: {error.message}</div>;
  }

  if (isLoading) {
    return <div className="text-center py-8">Loading breakfast menu items...</div>;
  }

  const categories = [
    { key: "entree", label: "Entrees", icon: "🍳" },
    { key: "side", label: "Sides", icon: "🥞" },
    { key: "beverage", label: "Beverages", icon: "☕" }
  ];

  return (
    <div>
      <div className="mb-6 flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Breakfast Menu Items</h2>
        <button
          onClick={() => setShowAddForm(true)}
          className="px-4 py-2 bg-[#62B5E5] text-white rounded hover:bg-[#007FA3] transition-colors"
        >
          Add Breakfast Item
        </button>
      </div>

      {/* Add Item Form */}
      {showAddForm && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-4">Add New Breakfast Item</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input
                type="text"
                value={newItem.name}
                onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2"
                placeholder="e.g., Scrambled Eggs"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
              <select
                value={newItem.category}
                onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2"
              >
                <option value="entree">Entree</option>
                <option value="side">Side</option>
                <option value="beverage">Beverage</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Day of Week</label>
              <select
                value={newItem.day_of_week === null ? "null" : newItem.day_of_week}
                onChange={(e) => setNewItem({ 
                  ...newItem, 
                  day_of_week: e.target.value === "null" ? null : Number(e.target.value) 
                })}
                className="w-full border border-gray-300 rounded px-3 py-2"
              >
                <option value="null">Every Day</option>
                {DAYS_OF_WEEK.map((day, idx) => (
                  <option key={idx} value={idx}>{day}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2"
                placeholder="Optional description"
              />
            </div>
          </div>
          <div className="mt-3">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={newItem.is_available}
                onChange={(e) => setNewItem({ ...newItem, is_available: e.target.checked })}
                className="rounded border-gray-300"
              />
              <span className="text-sm text-gray-700">Available</span>
            </label>
          </div>
          <div className="mt-4 flex gap-2">
            <button
              onClick={handleAddItem}
              disabled={!newItem.name}
              className="px-4 py-2 bg-[#007FA3] text-white rounded hover:bg-[#005f7a] disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Add Item
            </button>
            <button
              onClick={() => {
                setShowAddForm(false);
                setNewItem({ name: "", description: "", category: "entree", day_of_week: null, is_available: true });
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Items by Category */}
      <div className="space-y-6">
        {categories.map(({ key, label, icon }) => {
          const items = itemsByCategory[key] || [];

          return (
            <div key={key} className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <h3 className="font-semibold text-gray-900">
                  <span className="mr-2">{icon}</span>
                  {label}
                </h3>
              </div>

              {items.length > 0 ? (
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200 bg-gray-50">
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Name</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Day</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-700">Description</th>
                      <th className="text-center py-3 px-4 font-semibold text-gray-700">Available</th>
                      <th className="text-right py-3 px-4 font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item: any) => {
                      const isEditing = editingItem === item.id;

                      return (
                        <tr key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4">
                            {isEditing ? (
                              <input
                                type="text"
                                value={editValues.name}
                                onChange={(e) => setEditValues({ ...editValues, name: e.target.value })}
                                className="w-full border border-gray-300 rounded px-2 py-1"
                              />
                            ) : (
                              <span className={item.is_available ? "text-gray-900 font-medium" : "text-gray-400"}>
                                {item.name}
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            {isEditing ? (
                              <select
                                value={editValues.day_of_week === null ? "null" : editValues.day_of_week}
                                onChange={(e) => setEditValues({ 
                                  ...editValues, 
                                  day_of_week: e.target.value === "null" ? null : Number(e.target.value) 
                                })}
                                className="w-full border border-gray-300 rounded px-2 py-1"
                              >
                                <option value="null">Every Day</option>
                                {DAYS_OF_WEEK.map((day, idx) => (
                                  <option key={idx} value={idx}>{day}</option>
                                ))}
                              </select>
                            ) : (
                              <span className={`text-sm ${item.is_available ? "text-gray-700" : "text-gray-400"}`}>
                                {getDayName(item.day_of_week)}
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4">
                            {isEditing ? (
                              <input
                                type="text"
                                value={editValues.description}
                                onChange={(e) => setEditValues({ ...editValues, description: e.target.value })}
                                className="w-full border border-gray-300 rounded px-2 py-1"
                              />
                            ) : (
                              <span className={item.is_available ? "text-gray-600 text-sm" : "text-gray-400 text-sm"}>
                                {item.description || "-"}
                              </span>
                            )}
                          </td>
                          <td className="py-3 px-4 text-center">
                            <button
                              onClick={() => handleToggleAvailable(item.id, item.is_available)}
                              className={`px-3 py-1 rounded text-xs font-medium ${
                                item.is_available
                                  ? "bg-green-100 text-green-800"
                                  : "bg-gray-100 text-gray-600"
                              }`}
                            >
                              {item.is_available ? "Available" : "Unavailable"}
                            </button>
                          </td>
                          <td className="py-3 px-4 text-right">
                            <div className="flex gap-2 justify-end">
                              {isEditing ? (
                                <>
                                  <button
                                    onClick={() => handleSave(item.id)}
                                    className="text-[#007FA3] hover:text-[#005f7a] font-medium text-sm"
                                  >
                                    Save
                                  </button>
                                  <button
                                    onClick={() => setEditingItem(null)}
                                    className="text-gray-600 hover:text-gray-800 font-medium text-sm"
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    onClick={() => handleEdit(item)}
                                    className="text-[#007FA3] hover:text-[#005f7a] font-medium text-sm"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleDelete(item.id)}
                                    className="text-red-600 hover:text-red-800 font-medium text-sm"
                                  >
                                    Delete
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <div className="px-4 py-8 text-center text-gray-500 text-sm">
                  No {label.toLowerCase()} added yet. Add one to get started.
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
// Special Weeks Tab Component (5-week rotating specials)
function SpecialWeeksTab() {
  // Get current week from the API
  const { menu: todaysMenu } = useTodaysMenu();
  const currentWeek = todaysMenu?.week_number || 1;

  const [selectedWeek, setSelectedWeek] = useState(currentWeek);
  const [editingItem, setEditingItem] = useState<number | null>(null);
  const [optionsFor, setOptionsFor] = useState<{ kind: string; id: number; name: string } | null>(null);
  const [editValues, setEditValues] = useState({ menu_item: "", description: "" });
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItem, setNewItem] = useState({ day_of_week: 1, menu_item: "", description: "" });

  // Update selected week when current week is loaded
  useEffect(() => {
    if (currentWeek) {
      setSelectedWeek(currentWeek);
    }
  }, [currentWeek]);

  const { data, isLoading } = useAdmin.useSpecialWeeks();
  const updateMutation = useAdmin.useUpdateSpecialWeekItem();
  const createMutation = useAdmin.useCreateSpecialWeekItem();
  const deleteMutation = useAdmin.useDeleteSpecialWeekItem();

  const weekItems = data?.weeks[selectedWeek] || [];

  const handleEdit = (item: any) => {
    setEditingItem(item.id);
    setEditValues({ menu_item: item.menu_item, description: item.description || "" });
  };

  const handleSave = async (itemId: number) => {
    await updateMutation.mutateAsync({ itemId, data: editValues });
    setEditingItem(null);
  };

  const handleToggleActive = async (itemId: number, currentStatus: boolean) => {
    await updateMutation.mutateAsync({ itemId, data: { is_active: !currentStatus } });
  };

  const handleDelete = async (itemId: number) => {
    if (confirm("Are you sure you want to delete this special item?")) {
      await deleteMutation.mutateAsync(itemId);
    }
  };

  const handleAddItem = async () => {
    await createMutation.mutateAsync({
      week_number: selectedWeek,
      day_of_week: newItem.day_of_week,
      menu_item: newItem.menu_item,
      description: newItem.description || undefined,
    });
    setShowAddForm(false);
    setNewItem({ day_of_week: 1, menu_item: "", description: "" });
  };

  if (isLoading) {
    return <div className="text-center py-8">Loading special weeks...</div>;
  }

  return (
    <div>
      {optionsFor && (<ItemOptionsEditor target={optionsFor} onClose={() => setOptionsFor(null)} />)}
      <div className="mb-4">
        <p className="text-sm text-gray-600">
          Configure the 5-week rotating specials menu. These specials follow the same rotation as the regular lunch menu.
          Use "Daily Specials" tab to add one-time specials for specific dates (which override rotating specials).
        </p>
      </div>

      {/* Week Selector */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex gap-2">
          {[1, 2, 3, 4, 5].map((week) => (
            <button
              key={week}
              onClick={() => setSelectedWeek(week)}
              className={`px-4 py-2 rounded font-medium transition-colors relative ${
                selectedWeek === week
                  ? "bg-[#007FA3] text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Week {week}
              {week === currentWeek && (
                <span className="ml-1.5 inline-flex items-center justify-center w-2 h-2 rounded-full bg-green-400 absolute -top-1 -right-1">
                  <span className="sr-only">Current week</span>
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-4">
          {currentWeek && (
            <div className="text-sm text-gray-600 flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-green-400"></span>
              <span>Current Week: {currentWeek}</span>
            </div>
          )}
          <button
            onClick={() => setShowAddForm(true)}
            className="px-4 py-2 bg-[#62B5E5] text-white rounded hover:bg-[#007FA3] transition-colors"
          >
            Add Special Item
          </button>
        </div>
      </div>

      {/* Add Item Form */}
      {showAddForm && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-4">Add New Special Item for Week {selectedWeek}</h3>
          <div className="grid grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Day of Week</label>
              <select
                value={newItem.day_of_week}
                onChange={(e) => setNewItem({ ...newItem, day_of_week: Number(e.target.value) })}
                className="w-full border border-gray-300 rounded px-3 py-2"
              >
                {DAYS_OF_WEEK.map((day, idx) => (
                  <option key={idx} value={idx}>
                    {day}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Special Item</label>
              <input
                type="text"
                value={newItem.menu_item}
                onChange={(e) => setNewItem({ ...newItem, menu_item: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2"
                placeholder="e.g., BBQ Pulled Pork"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <input
                type="text"
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2"
                placeholder="Optional description"
              />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button
              onClick={handleAddItem}
              disabled={!newItem.menu_item}
              className="px-4 py-2 bg-[#007FA3] text-white rounded hover:bg-[#005f7a] disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Add Item
            </button>
            <button
              onClick={() => {
                setShowAddForm(false);
                setNewItem({ day_of_week: 1, menu_item: "", description: "" });
              }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Menu Items Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 font-semibold text-gray-700">Day</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700">Special Item</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700">Description</th>
              <th className="text-center py-3 px-4 font-semibold text-gray-700">Active</th>
              <th className="text-right py-3 px-4 font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {DAYS_OF_WEEK.map((day, dayIdx) => {
              const item = weekItems.find((i: any) => i.day_of_week === dayIdx);
              const isEditing = editingItem === item?.id;

              return (
                <tr key={dayIdx} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-3 px-4 font-medium text-gray-900">{day}</td>
                  <td className="py-3 px-4">
                    {item ? (
                      isEditing ? (
                        <input
                          type="text"
                          value={editValues.menu_item}
                          onChange={(e) => setEditValues({ ...editValues, menu_item: e.target.value })}
                          className="w-full border border-gray-300 rounded px-2 py-1"
                        />
                      ) : (
                        <span className={item.is_active ? "text-gray-900" : "text-gray-400"}>
                          {item.menu_item}
                        </span>
                      )
                    ) : (
                      <span className="text-gray-400 italic">No special</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    {item ? (
                      isEditing ? (
                        <input
                          type="text"
                          value={editValues.description}
                          onChange={(e) => setEditValues({ ...editValues, description: e.target.value })}
                          className="w-full border border-gray-300 rounded px-2 py-1"
                        />
                      ) : (
                        <span className={item.is_active ? "text-gray-600 text-sm" : "text-gray-400 text-sm"}>
                          {item.description || "-"}
                        </span>
                      )
                    ) : null}
                  </td>
                  <td className="py-3 px-4 text-center">
                    {item && (
                      <button
                        onClick={() => handleToggleActive(item.id, item.is_active)}
                        className={`px-3 py-1 rounded text-xs font-medium ${
                          item.is_active
                            ? "bg-green-100 text-green-800"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {item.is_active ? "Active" : "Inactive"}
                      </button>
                    )}
                  </td>
                  <td className="py-3 px-4 text-right">
                    {item && (
                      <div className="flex gap-2 justify-end">
                        {isEditing ? (
                          <>
                            <button
                              onClick={() => handleSave(item.id)}
                              className="text-[#007FA3] hover:text-[#005f7a] font-medium text-sm"
                            >
                              Save
                            </button>
                            <button
                              onClick={() => setEditingItem(null)}
                              className="text-gray-600 hover:text-gray-800 font-medium text-sm"
                            >
                              Cancel
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              onClick={() => handleEdit(item)}
                              className="text-[#007FA3] hover:text-[#005f7a] font-medium text-sm"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => setOptionsFor({ kind: "special_week", id: item.id, name: item.menu_item })}
                              className="text-indigo-600 hover:text-indigo-800 font-medium text-sm"
                            >
                              Options
                            </button>
                            <button
                              onClick={() => handleDelete(item.id)}
                              className="text-red-600 hover:text-red-800 font-medium text-sm"
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =============================================================================
// Item Options Editor - "dropdown with checkboxes" per menu item
// =============================================================================

function ItemOptionsEditor({
  target,
  onClose,
}: {
  target: { kind: string; id: number; name: string };
  onClose: () => void;
}) {
  const [prompt, setPrompt] = useState("");
  const [rows, setRows] = useState<string[]>(["", ""]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const token = sessionStorage.getItem("hmh_access_token");
        const url = new URL(`${API_BASE_URL}/admin/menu/item-options`, window.location.origin);
        url.searchParams.append("item_kind", target.kind);
        url.searchParams.append("item_id", String(target.id));
        const r = await fetch(url.toString(), { headers: { Authorization: `Bearer ${token}` } });
        if (r.ok) {
          const d = await r.json();
          setPrompt(d.prompt || "");
          const names = (d.options || []).map((o: { name: string }) => o.name);
          setRows(names.length ? names : ["", ""]);
        }
      } catch {
        setError("Failed to load options");
      } finally {
        setLoading(false);
      }
    })();
  }, [target]);

  const setRow = (i: number, value: string) =>
    setRows((prev) => prev.map((r, idx) => (idx === i ? value : r)));
  const removeRow = (i: number) =>
    setRows((prev) => (prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev));
  const addRow = () => setRows((prev) => [...prev, ""]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const token = sessionStorage.getItem("hmh_access_token");
      const r = await fetch(`${API_BASE_URL}/admin/menu/item-options`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          item_kind: target.kind,
          item_id: target.id,
          prompt: prompt.trim() || null,
          names: rows.map((n) => n.trim()).filter(Boolean),
        }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.detail || "Failed to save options");
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to save options");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold text-gray-900 mb-1">Selectable options</h3>
        <p className="text-sm text-gray-500 mb-4">
          For <b>{target.name}</b>. When this item has options, staff must pick at
          least one when ordering (like the salad toppings), and their choice shows
          on the kitchen list and printouts. Leave all lines empty for no options.
        </p>
        {loading ? (
          <p className="text-gray-500">Loading...</p>
        ) : (
          <>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Question shown to staff
            </label>
            <input
              type="text"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g. Choose your sandwich"
              maxLength={120}
              className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-hmh-blue"
            />
            <label className="block text-sm font-medium text-gray-700 mb-2">Options</label>
            <div className="space-y-2 mb-2">
              {rows.map((value, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={value}
                    onChange={(e) => setRow(i, e.target.value)}
                    placeholder={i === 0 ? "e.g. 3 Cheese Grilled Cheese" : i === 1 ? "e.g. Ham & Cheese" : `Option ${i + 1}`}
                    maxLength={100}
                    className="flex-1 border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-hmh-blue"
                  />
                  <button
                    type="button"
                    onClick={() => removeRow(i)}
                    disabled={rows.length <= 1}
                    className="p-2 text-gray-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                    aria-label="Remove option"
                    title="Remove option"
                  >
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addRow}
              disabled={rows.length >= 20}
              className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-[#007FA3] hover:text-[#005f7a] disabled:opacity-40"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add another option
            </button>
            {error && <p className="text-red-600 text-sm mb-3">{error}</p>}
            <div className="flex gap-2 justify-end">
              <button onClick={onClose} className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300">
                Cancel
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="px-4 py-2 bg-[#007FA3] text-white rounded hover:bg-[#005f7a] disabled:bg-gray-300"
              >
                {saving ? "Saving..." : "Save Options"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Midnight Menu Tab - overnight-shift menu items + what the night page offers
// =============================================================================

function MidnightMenuTab() {
  const { data: settings, isLoading: settingsLoading } = useMidnightSettings();
  const updateSettings = useUpdateMidnightSettings();

  const today = new Date().toISOString().split("T")[0];
  const horizon = new Date(Date.now() + 30 * 86400000).toISOString().split("T")[0];
  const { data, isLoading, refetch } = useAdmin.useDailySpecials(today, horizon);
  const createMutation = useAdmin.useCreateDailySpecial();
  const deleteMutation = useAdmin.useDeleteDailySpecial();

  const [optionsFor, setOptionsFor] = useState<{ kind: string; id: number; name: string } | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newItem, setNewItem] = useState({ date: today, menu_item: "", description: "", recurring: false });
  const [saveNote, setSaveNote] = useState<string | null>(null);

  useEffect(() => {
    const token = sessionStorage.getItem("hmh_access_token");
    if (token) refetch();
  }, [refetch]);

  const midnightSpecials = (data?.specials || [])
    .filter((sp: any) => sp.meal_period === "midnight")
    .sort((a: any, b: any) => (b.recurring ? 1 : 0) - (a.recurring ? 1 : 0));

  const toggleSetting = async (key: "allow_salad" | "offer_special", value: boolean) => {
    setSaveNote(null);
    await updateSettings.mutateAsync({ [key]: value });
    setSaveNote("Saved");
    setTimeout(() => setSaveNote(null), 2000);
  };

  const handleAdd = async () => {
    await createMutation.mutateAsync({ ...newItem, meal_period: "midnight" });
    setShowAddForm(false);
    setNewItem({ date: today, menu_item: "", description: "", recurring: false });
  };

  const handleDelete = async (id: number) => {
    if (confirm("Remove this midnight menu item?")) {
      await deleteMutation.mutateAsync(id);
    }
  };

  if (settingsLoading || isLoading) {
    return <div className="text-center py-8">Loading midnight menu...</div>;
  }

  return (
    <div>
      {optionsFor && (<ItemOptionsEditor target={optionsFor} onClose={() => setOptionsFor(null)} />)}

      <div className="mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">🌙 Midnight Menu</h2>
          <p className="text-sm text-gray-500 mt-1">
            What the overnight shift can order on the midnight page. Staff order up to a
            week ahead; items here show on their chosen night.
          </p>
        </div>
        <button
          onClick={() => setShowAddForm(true)}
          className="px-4 py-2 bg-[#62B5E5] text-white rounded hover:bg-[#007FA3] transition-colors"
        >
          Add Menu Item
        </button>
      </div>

      {!settings?.enabled && (
        <div className="mb-6 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900 text-sm">
          Midnight ordering is currently <b>turned off</b> — staff can't see any of this yet.
          Enable it under <b>Admin → Printing → Midnight Special</b> when the kitchen is ready.
        </div>
      )}

      {/* What the night page offers */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <h3 className="font-semibold text-gray-900 mb-3">Shown on the midnight page</h3>
        <div className="flex flex-wrap items-center gap-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={settings?.allow_salad ?? true}
              onChange={(e) => toggleSetting("allow_salad", e.target.checked)}
              className="h-5 w-5 rounded border-gray-300 text-hmh-blue focus:ring-hmh-blue"
            />
            <span className="text-sm text-gray-800">Display the salad picker</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={settings?.offer_special ?? false}
              onChange={(e) => toggleSetting("offer_special", e.target.checked)}
              className="h-5 w-5 rounded border-gray-300 text-hmh-blue focus:ring-hmh-blue"
            />
            <span className="text-sm text-gray-800">Display the menu items below</span>
          </label>
          {saveNote && <span className="text-sm text-green-700">{saveNote}</span>}
        </div>
      </div>

      {/* Add form */}
      {showAddForm && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="font-semibold text-gray-900 mb-4">Add Midnight Menu Item</h3>
          <div className="mb-4 flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="midnight-schedule"
                checked={!newItem.recurring}
                onChange={() => setNewItem({ ...newItem, recurring: false })}
                className="h-4 w-4 border-gray-300 text-hmh-blue focus:ring-hmh-blue"
              />
              <span className="text-sm text-gray-800">One night only</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="midnight-schedule"
                checked={newItem.recurring}
                onChange={() => setNewItem({ ...newItem, recurring: true })}
                className="h-4 w-4 border-gray-300 text-hmh-blue focus:ring-hmh-blue"
              />
              <span className="text-sm text-gray-800">Every night (recurring)</span>
            </label>
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {newItem.recurring ? "Starts on" : "Night of"}
              </label>
              <input
                type="date"
                min={today}
                value={newItem.date}
                onChange={(e) => setNewItem({ ...newItem, date: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-hmh-blue"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Item</label>
              <input
                type="text"
                value={newItem.menu_item}
                onChange={(e) => setNewItem({ ...newItem, menu_item: e.target.value })}
                placeholder="e.g. Midnight Breakfast Burrito"
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-hmh-blue"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
              <input
                type="text"
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-hmh-blue"
              />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button
              onClick={handleAdd}
              disabled={!newItem.date || !newItem.menu_item.trim()}
              className="px-4 py-2 bg-[#007FA3] text-white rounded hover:bg-[#005f7a] disabled:bg-gray-300 disabled:cursor-not-allowed"
            >
              Add Item
            </button>
            <button
              onClick={() => { setShowAddForm(false); setNewItem({ date: today, menu_item: "", description: "", recurring: false }); }}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Items list */}
      <div className="space-y-3">
        {midnightSpecials.length > 0 ? (
          midnightSpecials.map((sp: any) => (
            <div
              key={sp.id}
              className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 flex items-start justify-between"
            >
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <span className="text-sm font-medium text-gray-900">
                    {sp.recurring
                      ? `Every night${sp.date > today ? ` starting ${formatDateString(sp.date)}` : ""}`
                      : formatDateString(sp.date)}
                  </span>
                  {sp.recurring ? (
                    <span className="px-2 py-1 text-xs font-medium rounded bg-emerald-100 text-emerald-800">
                      🔁 Recurring
                    </span>
                  ) : (
                    <span className="px-2 py-1 text-xs font-medium rounded bg-indigo-100 text-indigo-800">
                      🌙 One night
                    </span>
                  )}
                </div>
                <p className="text-lg font-semibold text-gray-900">{sp.menu_item}</p>
                {sp.description && <p className="text-sm text-gray-500">{sp.description}</p>}
              </div>
              <div className="flex items-center">
                <button
                  onClick={() => setOptionsFor({ kind: "daily_special", id: sp.id, name: sp.menu_item })}
                  className="ml-4 text-indigo-600 hover:text-indigo-800 font-medium text-sm"
                >
                  Options
                </button>
                <button
                  onClick={() => handleDelete(sp.id)}
                  className="ml-4 text-red-600 hover:text-red-800 font-medium text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        ) : (
          <p className="text-gray-500 py-6 text-center">
            No midnight menu items scheduled. The night page shows
            {settings?.allow_salad ? " the salad picker" : " nothing"} until you add some.
          </p>
        )}
      </div>
    </div>
  );
}

